import { useCallback, useState } from 'react';
import { Card } from 'reactstrap';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Switch } from 'src/core/ui';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import { FulfillmentProviderClient } from 'src/WebApiController';

import * as styles from './FulfillmentProviders.css';

interface FulfillmentProviderCardProps {
  logo: JSX.Element | null;
  fulfillmentProviderSellerUserId: string;
  name: string;
  description: string;
  enabled: boolean;
}

export const FulfillmentProviderCard: React.FC<
  FulfillmentProviderCardProps
> = ({ logo, fulfillmentProviderSellerUserId, name, description, enabled }) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const [isSwitchChecked, setIsSwitchChecked] = useState(enabled);
  const { trackError } = useClientLogContext();

  const toggleFulfillmentProviderSetting = useCallback(
    async (isChecked: boolean) => {
      const originalState = isSwitchChecked;
      setIsSwitchChecked(isChecked);

      await tryInvokeApi(
        async () => {
          const fulfillmentProviderClient = new FulfillmentProviderClient(
            activeAccountWebClientConfig
          );

          const succeeded =
            await fulfillmentProviderClient.toggleFulfillmentProviderSetting(
              fulfillmentProviderSellerUserId,
              isChecked
            );

          if (!succeeded) {
            setIsSwitchChecked(originalState);
          }
        },
        (error) => {
          setIsSwitchChecked(originalState);
          trackError(
            'FulfillmentProviderClient.ToggleFulfillmentProviderSetting',
            error
          );
        }
      );
    },
    [
      activeAccountWebClientConfig,
      fulfillmentProviderSellerUserId,
      isSwitchChecked,
      trackError,
    ]
  );

  return (
    <Card className={styles.card}>
      <div className={styles.header}>
        {logo && <div className={styles.logoContainer}>{logo}</div>}
        <h3 className={styles.providerName}>{name}</h3>
      </div>

      <div className={styles.providerDescription}>{description}</div>

      <div className={styles.footer}>
        <Switch
          checked={isSwitchChecked}
          key={fulfillmentProviderSellerUserId}
          onChange={(e) => e.stopPropagation()}
          onCheckedChange={(isChecked) => {
            toggleFulfillmentProviderSetting(isChecked);
          }}
        />
      </div>
    </Card>
  );
};
